import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Box, Grid, Card, CardContent, CardActionArea, Chip,
  CircularProgress, Button,
  Snackbar, Alert, Avatar, Skeleton, Pagination, LinearProgress,
  alpha, useTheme,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SendIcon from '@mui/icons-material/Send';
import HistoryIcon from '@mui/icons-material/History';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GroupsIcon from '@mui/icons-material/Groups';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useUser } from '../contexts/UserContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import LockIcon from '@mui/icons-material/Lock';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import DatasetIcon from '@mui/icons-material/Dataset';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Tips from '../components/dialogs/Tips';
import HubSubmitDialog from '../components/dialogs/HubSubmitDialog';
import CharacterDatabaseView from '../components/hub/CharacterDatabaseView';
import {
  HubCampaignCard, HubCampaignDetail, HubAttribute,
  HubLeaderboardEntry, HubPointsHistory, HubOptions,
  hubGetCampaigns, hubGetCampaignDetail,
  hubGetLeaderboard, hubGetPointsHistory, hubGetOptions,
} from '../services/hubApi';

// ============================================================
// PULSE Lab Brand
// ============================================================
const PULSE_BLUE = '#4d7cff';
const PULSE_GRADIENT = `linear-gradient(135deg, #d9ff00, ${PULSE_BLUE})`;
const PULSE_GRADIENT_TEXT = PULSE_GRADIENT;

// Responsive container padding (余白なしテンプレート対応)
const contentPx = { xs: 2, sm: 3, md: 4 };
const contentMaxWidth = 1080;

// ============================================================
// Shared Styles
// ============================================================

const glassCard = {
  background: 'rgba(255,255,255,0.03)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: '16px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  },
};

const glassCardStatic = {
  background: 'rgba(255,255,255,0.03)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: '16px',
};


// ============================================================
// Submit Dialog — extracted to HubSubmitDialog component
// ============================================================


// ============================================================
// Campaign Detail View
// ============================================================

interface CampaignDetailViewProps {
  campaignId: number;
  onBack: () => void;
  locked?: boolean;
}

const CampaignDetailView: React.FC<CampaignDetailViewProps> = ({ campaignId, onBack, locked }) => {
  const theme = useTheme();
  const { isAuthenticated } = useUser();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<HubCampaignDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedAttr, setSelectedAttr] = useState<HubAttribute | null>(null);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '' });

  const loadCampaign = useCallback(async () => {
    setLoading(true);
    try { setCampaign(await hubGetCampaignDetail(campaignId)); } catch { /* handle */ }
    setLoading(false);
  }, [campaignId]);

  useEffect(() => { loadCampaign(); }, [loadCampaign]);

  const handleSubmitClick = (attr: HubAttribute) => {
    if (locked) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    setSelectedAttr(attr);
    setSubmitDialogOpen(true);
  };

  if (loading) return (
    <Box sx={{ px: contentPx, maxWidth: contentMaxWidth, mx: 'auto' }}>
      <Skeleton variant="rounded" width={120} height={36} sx={{ mb: 3, borderRadius: '100px' }} />
      <Skeleton variant="rounded" height={48} sx={{ borderRadius: 2, mb: 1.5 }} />
      <Skeleton variant="rounded" height={20} width="60%" sx={{ borderRadius: 1, mb: 4 }} />
      <Grid container spacing={2}>
        {[1, 2, 3, 4].map(i => (
          <Grid item xs={12} sm={6} key={i}>
            <Skeleton variant="rounded" height={180} sx={{ borderRadius: '16px' }} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  if (!campaign) return (
    <Box sx={{ textAlign: 'center', py: 8, px: contentPx }}>
      <Typography color="text.secondary">キャンペーンが見つかりません</Typography>
    </Box>
  );

  const backButton = (
    <Button
      onClick={onBack}
      size="small"
      startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
      sx={{
        mb: 3,
        borderRadius: '100px',
        px: 2,
        color: 'text.secondary',
        '&:hover': { background: 'rgba(255,255,255,0.05)' },
      }}
    >
      プロジェクト一覧
    </Button>
  );

  // database種別 (Wiki型 / 例: キャラクター) は専用ビューに分岐
  if (campaign.kind === 'database') {
    return (
      <Box sx={{ px: contentPx, maxWidth: contentMaxWidth, mx: 'auto' }}>
        {backButton}
        <CharacterDatabaseView campaign={campaign} locked={locked} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: contentPx, maxWidth: contentMaxWidth, mx: 'auto' }}>
      {/* Back button */}
      {backButton}

      {/* Campaign header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{
            background: PULSE_GRADIENT,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          {campaign.title}
        </Typography>
        {campaign.description && (
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600 }}>
            {campaign.description}
          </Typography>
        )}
      </Box>

      {/* Attributes grid */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
        <AutoAwesomeIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="body1" fontWeight={600}>
          データ項目
        </Typography>
        <Chip
          label={campaign.attributes?.length || 0}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 700,
            bgcolor: alpha(theme.palette.primary.main, 0.15),
            color: 'primary.main',
          }}
        />
      </Box>

      <Grid container spacing={2}>
        {campaign.attributes?.map(attr => {
          const progress = Math.min((attr.submission_count / attr.min_submissions) * 100, 100);
          const isComplete = attr.submission_count >= attr.min_submissions;
          return (
            <Grid item xs={12} sm={6} key={attr.id}>
              <Card
                variant="outlined"
                sx={{
                  ...glassCard,
                  height: '100%',
                  cursor: locked ? 'default' : 'pointer',
                }}
                onClick={() => handleSubmitClick(attr)}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight={600} sx={{ mb: 0.75 }}>
                        {attr.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={
                            attr.attribute_type === 'numeric' ? '数値' :
                            attr.attribute_type === 'boolean' ? 'はい/いいえ' :
                            attr.attribute_type === 'enum' ? '選択肢' : 'スケール'
                          }
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.65rem',
                            bgcolor: 'rgba(255,255,255,0.06)',
                            color: 'text.secondary',
                            border: 'none',
                          }}
                        />
                        {attr.data_mode === 'factual' && (
                          <Chip
                            label="ファクト"
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.65rem',
                              bgcolor: alpha(theme.palette.info.main, 0.15),
                              color: 'info.main',
                              border: 'none',
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    {attr.points_reward > 0 && (
                      <Chip
                        icon={<StarIcon sx={{ fontSize: '14px !important' }} />}
                        label={`${attr.points_reward}pt`}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          color: 'primary.main',
                          border: 'none',
                          '& .MuiChip-icon': { color: 'primary.main' },
                        }}
                      />
                    )}
                  </Box>

                  {attr.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.5 }}>
                      {attr.description}
                    </Typography>
                  )}

                  {/* Progress bar */}
                  <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">データ進捗</Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{ color: isComplete ? 'success.main' : 'primary.main' }}
                      >
                        {Math.round(progress)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{
                        height: 4,
                        borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.06)',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 2,
                          background: isComplete
                            ? theme.palette.success.main
                            : `linear-gradient(90deg, ${theme.palette.primary.main}, ${PULSE_BLUE})`,
                        },
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {attr.note_mode !== 'disabled' && (
                        <Chip
                          label={attr.note_mode === 'required' ? '補足必須' : '補足可'}
                          size="small"
                          sx={{ height: 20, fontSize: '0.6rem', bgcolor: 'rgba(255,255,255,0.04)', border: 'none' }}
                        />
                      )}
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={locked}
                      endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                      sx={{
                        borderRadius: '100px',
                        px: 2,
                        py: 0.5,
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        background: PULSE_GRADIENT,
                        color: '#000',
                        fontWeight: 600,
                        boxShadow: 'none',
                        '&:hover': {
                          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                        },
                        '&.Mui-disabled': {
                          background: 'rgba(255,255,255,0.08)',
                          color: 'rgba(255,255,255,0.3)',
                        },
                      }}
                      onClick={(e) => { e.stopPropagation(); handleSubmitClick(attr); }}
                    >
                      {locked ? '停止中' : '投稿'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <HubSubmitDialog
        open={submitDialogOpen}
        onClose={() => setSubmitDialogOpen(false)}
        attribute={selectedAttr}
        onSubmitted={() => {
          setSnack({ open: true, message: '送信しました！' });
        }}
      />

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled">{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

// ============================================================
// Main Hub Page
// ============================================================

const HubPage: React.FC = () => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(parseInt(searchParams.get('tab') || '0'));
  const selectedCampaign = searchParams.get('campaign');

  const [hubOptions, setHubOptions] = useState<HubOptions | null>(null);
  const [campaigns, setCampaigns] = useState<HubCampaignCard[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [leaderboard, setLeaderboard] = useState<HubLeaderboardEntry[]>([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const { isAuthenticated, user } = useUser();
  const [pointsHistory, setPointsHistory] = useState<HubPointsHistory[]>([]);
  const [pointsPage, setPointsPage] = useState(1);
  const [pointsCount, setPointsCount] = useState(0);
  const [loadingPoints, setLoadingPoints] = useState(false);

  useEffect(() => {
    (async () => {
      setLoadingCampaigns(true);
      try {
        const [camps, opts] = await Promise.all([hubGetCampaigns(), hubGetOptions()]);
        setCampaigns(camps);
        setHubOptions(opts);
      } catch { /* */ }
      setLoadingCampaigns(false);
    })();
  }, []);

  useEffect(() => {
    if (tab === 1 && user?.is_superuser) {
      (async () => {
        setLoadingLeaderboard(true);
        try { setLeaderboard(await hubGetLeaderboard()); } catch { /* */ }
        setLoadingLeaderboard(false);
      })();
    }
  }, [tab]);

  useEffect(() => {
    if (tab === 2 && isAuthenticated) {
      (async () => {
        setLoadingPoints(true);
        try {
          const res = await hubGetPointsHistory(pointsPage);
          setPointsHistory(res.results);
          setPointsCount(res.count);
        } catch { /* */ }
        setLoadingPoints(false);
      })();
    }
  }, [tab, pointsPage, isAuthenticated]);

  const handleTabChange = (v: number) => {
    setTab(v);
    setSearchParams({ tab: String(v) });
  };

  const handleSelectCampaign = (id: number) => {
    setSearchParams({ campaign: String(id) });
  };

  const handleBackToCampaigns = () => {
    setSearchParams({});
  };

  // Campaign Detail
  if (selectedCampaign) {
    return (
      <>
        <Helmet><title>PULSE Lab</title></Helmet>
        <CampaignDetailView campaignId={Number(selectedCampaign)} onBack={handleBackToCampaigns} locked={hubOptions?.lock?.locked} />
      </>
    );
  }

  const tabItems = [
    { icon: <RocketLaunchIcon sx={{ fontSize: 16 }} />, label: 'キャンペーン' },
    { icon: <LeaderboardIcon sx={{ fontSize: 16 }} />, label: 'リーダーボード' },
    ...(isAuthenticated ? [{ icon: <HistoryIcon sx={{ fontSize: 16 }} />, label: 'ポイント履歴' }] : []),
  ];

  return (
    <>
      <Helmet><title>PULSE Lab</title></Helmet>
      <Box>
        {/* ============================================================ */}
        {/* Hero Section (full-width, handles own padding) */}
        {/* ============================================================ */}
        <Box
          sx={{
            position: 'relative',
            pt: { xs: 4, sm: 5 },
            pb: { xs: 4, sm: 5 },
            mb: 0,
            overflow: 'hidden',
            px: contentPx,
          }}
        >
          {/* Ambient glow - dual color */}
          <Box
            sx={{
              position: 'absolute',
              top: '-50%',
              left: '30%',
              transform: 'translateX(-50%)',
              width: '500px',
              height: '350px',
              background: `radial-gradient(ellipse, ${alpha(theme.palette.primary.main, 0.07)} 0%, transparent 70%)`,
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '-30%',
              left: '70%',
              transform: 'translateX(-50%)',
              width: '400px',
              height: '300px',
              background: `radial-gradient(ellipse, ${alpha(PULSE_BLUE, 0.05)} 0%, transparent 70%)`,
              pointerEvents: 'none',
            }}
          />

          <Box sx={{ position: 'relative', textAlign: 'center' }}>
            {/* Badge */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 2,
                py: 0.5,
                borderRadius: '100px',
                border: '1px solid rgba(255,255,255,0.1)',
                background: 'rgba(255,255,255,0.04)',
                mb: 2.5,
              }}
            >
              <GroupsIcon sx={{ fontSize: 14, color: PULSE_BLUE }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, letterSpacing: 0.5 }}>
                Community-Powered Database
              </Typography>
            </Box>

            {/* Title */}
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                background: PULSE_GRADIENT_TEXT,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 0.5,
                letterSpacing: '-0.02em',
                fontSize: { xs: '1.8rem', sm: '2.5rem' },
              }}
            >
              PULSE Lab.
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'rgb(214, 214, 214)',
                maxWidth: 440,
                mx: 'auto',
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              コミュニティの知見を集約・分析。
              <br />
              情報を構造化し、利用可能なデータへ。
            </Typography>

            <Tips
              title="PULSE Lab とは？"
              maxWidth="sm"
              buttonComponent={
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.5,
                    py: 0,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    background: 'rgba(255,255,255,0.03)',
                    transition: 'all 0.2s ease',
                    mb: 1,
                    '&:hover': {
                      background: 'rgba(255,255,255,0.06)',
                      borderColor: 'rgba(255,255,255,0.15)',
                    },
                  }}
                >
                  <HelpOutlineIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.4)' }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>
                    PULSE Lab とは？
                  </Typography>
                </Box>
              }
              content={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      プロジェクトの目的
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.3 }}>
                      PULSE Labは、通常のストア情報や既存の情報源からは得られにくい「ゲームのより深いメタ情報」を蓄積するためのデータベース拡張プロジェクトです。
                      コアゲーマーが集まるPULSEコミュニティの集合知を活用し、例えば以下のようなデータの募集・分析を行います。
                    </Typography>
                    <Box component="ul" sx={{ mt: 1, mb:0, pl: 2.5, lineHeight: 1.3, color: 'rgba(255,255,255,0.6)', '& li': { mb: 0.5, fontSize: '0.7rem' } }}>
                      <li>ホラーゲームにおけるジャンプスケア（びっくり要素）の有無や恐怖度の数値化</li>
                      <li>アクセシビリティ（難易度設定など）のバリエーション</li>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      データの活用とエコシステムの構築
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.3 }}>
                      集計・分析されたデータは、PULSEの情報強化はもちろんのこと、特定のニーズに特化した単独ツールとしても外部へ向けて順次展開されます。
                      例えば「ジャンプスケアや恐怖度を事前に調べられるツール」や「難易度バリエーション付きのゲーム検索」など、まだPULSEを知らない層がPULSEを知る入り口となる巨大なエコシステムを構築していくことが狙いです。
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      貢献の可視化
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.3 }}>
                      上述の通りLabへの参加は、PULSEの成長を加速させる「開発支援」に近い活動です。全ての情報提供や校閲などの活動には専用の貢献ポイントが付与され、今後実装予定のプロフィール上における実績システムに完全統合されます。
                    </Typography>
                  </Box>
                </Box>
              }
            />

            {/* Stats bar */}
            {!loadingCampaigns && campaigns.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: { xs: 3, sm: 5 },
                  mt: 1,
                }}
              >
                {[
                  { label: 'キャンペーン', value: campaigns.length, icon: <RocketLaunchIcon sx={{ fontSize: 14 }} /> },
                  { label: 'データ項目', value: campaigns.reduce((sum, c) => sum + c.attribute_count, 0), icon: <DatasetIcon sx={{ fontSize: 14 }} /> },
                  ...(user ? [{ label: 'あなたの貢献', value: user.contribution_points, icon: <StarIcon sx={{ fontSize: 14 }} /> }] : []),
                ].map((stat, i) => (
                  <Box key={i} sx={{ textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.25 }}>
                      <Box sx={{ color: 'rgba(255,255,255,0.35)' }}>{stat.icon}</Box>
                      <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem' }}>
                        {stat.value}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.65rem' }}>
                      {stat.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* Hub Lock */}
        {hubOptions?.lock?.locked && (
          <Box sx={{ px: contentPx, maxWidth: contentMaxWidth, mx: 'auto', mb: 3 }}>
            <Alert
              severity="warning"
              icon={<LockIcon />}
              sx={{
                borderRadius: '12px',
                border: '1px solid rgba(255,152,0,0.2)',
                bgcolor: 'rgba(255,152,0,0.06)',
              }}
            >
              {hubOptions.lock.message}
            </Alert>
          </Box>
        )}

        {/* Hub Announcement */}
        {hubOptions?.announcement?.enabled && hubOptions.announcement.text && (
          <Box sx={{ px: contentPx, maxWidth: contentMaxWidth, mx: 'auto', mb: 3 }}>
            <Alert
              severity={hubOptions.announcement.severity || 'info'}
              sx={{
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.08)',
                bgcolor: 'rgba(255,255,255,0.03)',
              }}
            >
              {hubOptions.announcement.text}
            </Alert>
          </Box>
        )}

        {/* ============================================================ */}
        {/* Pill Tabs (横スクロール対応) */}
        {/* ============================================================ */}
        <Box
          sx={{
            px: contentPx,
            maxWidth: contentMaxWidth,
            mx: 'auto',
            mb: 4,
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 0.75,
              justifyContent: 'center',
              width: 'fit-content',
              minWidth: '100%',
            }}
          >
            {tabItems.map((item, i) => {
              const active = tab === i;
              return (
                <Box
                  key={i}
                  onClick={() => handleTabChange(i)}
                  sx={{
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.5, sm: 0.625 },
                    borderRadius: '100px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    transition: 'all 0.2s ease',
                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                    fontWeight: active ? 600 : 400,
                    color: active ? '#d9ff00' : 'rgba(255,255,255,0.55)',
                    background: active ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.04)',
                    border: '1px solid',
                    borderColor: active ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.08)',
                    boxShadow: 'none',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    '&:hover': active ? {} : {
                      background: 'rgba(255,255,255,0.06)',
                      color: 'rgba(255,255,255,0.85)',
                    },
                  }}
                >
                  {item.icon}
                  <Box
                    component="span"
                    sx={{
                      ...(active && {
                        background: PULSE_GRADIENT_TEXT,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }),
                    }}
                  >
                    {item.label}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ============================================================ */}
        {/* Tab 0: Campaigns */}
        {/* ============================================================ */}
        {tab === 0 && (
          <Box sx={{ px: contentPx, maxWidth: contentMaxWidth, mx: 'auto' }}>
            {loadingCampaigns ? (
              <Grid container spacing={2.5}>
                {[1, 2, 3].map(i => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Skeleton variant="rounded" height={220} sx={{ borderRadius: '16px' }} />
                  </Grid>
                ))}
              </Grid>
            ) : campaigns.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2.5,
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <InboxOutlinedIcon sx={{ fontSize: 28, color: 'rgba(255,255,255,0.2)' }} />
                </Box>
                <Typography variant="body2" fontWeight={500} sx={{ color: 'rgba(255,255,255,0.5)', mb: 0.5 }}>
                  現在アクティブなキャンペーンはありません
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                  新しいキャンペーンが開始されるとここに表示されます
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2.5}>
                {campaigns.map(c => (
                  <Grid item xs={12} sm={6} md={4} key={c.id}>
                    <Card
                      sx={{
                        ...glassCard,
                        height: '100%',
                        overflow: 'hidden',
                      }}
                    >
                      <CardActionArea
                        onClick={() => handleSelectCampaign(c.id)}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'stretch',
                        }}
                      >
                        {/* Card image */}
                        {c.image_url ? (
                          <Box
                            sx={{
                              height: 140,
                              position: 'relative',
                              background: `url(${c.image_url})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                inset: 0,
                                background: 'linear-gradient(to bottom, transparent 30%, rgba(0,0,0,0.8) 100%)',
                              },
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: 80,
                              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(PULSE_BLUE, 0.08)})`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <RocketLaunchIcon sx={{ fontSize: 28, color: alpha(theme.palette.primary.main, 0.4) }} />
                          </Box>
                        )}

                        <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="body1" fontWeight={700} sx={{ flex: 1, pr: 1 }}>
                              {c.title}
                            </Typography>
                            {c.is_permanent ? (
                              <Chip
                                icon={<AllInclusiveIcon sx={{ fontSize: '14px !important' }} />}
                                label="常設"
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                                  color: 'primary.main',
                                  border: 'none',
                                  '& .MuiChip-icon': { color: 'primary.main' },
                                }}
                              />
                            ) : (
                              <Chip
                                icon={<AccessTimeIcon sx={{ fontSize: '14px !important' }} />}
                                label="期間限定"
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  bgcolor: alpha('#ff9800', 0.12),
                                  color: '#ff9800',
                                  border: 'none',
                                  '& .MuiChip-icon': { color: '#ff9800' },
                                }}
                              />
                            )}
                          </Box>

                          {c.description && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(255,255,255,0.5)',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.5,
                                mb: 1.5,
                              }}
                            >
                              {c.description}
                            </Typography>
                          )}

                          <Box sx={{ flex: 1 }} />

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              pt: 1.5,
                              borderTop: '1px solid rgba(255,255,255,0.05)',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <DatasetIcon sx={{ fontSize: 13 }} />
                                {c.kind === 'database' ? 'データベース' : `${c.attribute_count} 項目`}
                              </Typography>
                              {!c.is_permanent && c.end_date && (
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <AccessTimeIcon sx={{ fontSize: 13 }} />
                                  {new Date(c.end_date).toLocaleDateString('ja-JP')}
                                </Typography>
                              )}
                            </Box>
                            <ArrowForwardIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.25)', transition: 'all 0.2s', '.MuiCardActionArea-root:hover &': { color: 'primary.main', transform: 'translateX(2px)' } }} />
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* ============================================================ */}
        {/* Tab 1: Leaderboard */}
        {/* ============================================================ */}
        {tab === 1 && !user?.is_superuser && (
          <Box sx={{ maxWidth: 600, mx: 'auto', px: contentPx, textAlign: 'center', py: 10 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2.5,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <LeaderboardIcon sx={{ fontSize: 28, color: 'rgba(255,255,255,0.2)' }} />
            </Box>
            <Typography variant="body2" fontWeight={500} sx={{ color: 'rgba(255,255,255,0.5)', mb: 0.5 }}>
              準備中
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              リーダーボードは近日公開予定です
            </Typography>
          </Box>
        )}

        {tab === 1 && user?.is_superuser && (
          <Box sx={{ maxWidth: 600, mx: 'auto', px: contentPx }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5,
                  background: alpha(PULSE_BLUE, 0.1),
                  border: `1px solid ${alpha(PULSE_BLUE, 0.2)}`,
                }}
              >
                <EmojiEventsIcon sx={{ fontSize: 20, color: PULSE_BLUE }} />
              </Box>
              <Typography variant="body1" fontWeight={700}>
                貢献リーダーボード
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mt: 0.5, display: 'block' }}>
                コミュニティへの貢献度ランキング
              </Typography>
            </Box>

            {loadingLeaderboard ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[1, 2, 3, 4, 5].map(i => (
                  <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: '12px' }} />
                ))}
              </Box>
            ) : leaderboard.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <LeaderboardIcon sx={{ fontSize: 24, color: 'rgba(255,255,255,0.2)' }} />
                </Box>
                <Typography variant="body2" fontWeight={500} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  まだランキングデータがありません
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {leaderboard.map((entry, i) => {
                  const isTop3 = i < 3;
                  const medalColors = ['#FFD700', '#C0C0C0', '#CD7F32'];
                  return (
                    <Box
                      key={i}
                      sx={{
                        ...glassCardStatic,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        px: 2.5,
                        py: 1.5,
                        ...(isTop3 && {
                          border: `1px solid ${alpha(medalColors[i], 0.3)}`,
                          background: alpha(medalColors[i], 0.04),
                        }),
                      }}
                    >
                      {/* Rank */}
                      {isTop3 ? (
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `linear-gradient(135deg, ${alpha(medalColors[i], 0.25)}, ${alpha(medalColors[i], 0.08)})`,
                            border: `1.5px solid ${alpha(medalColors[i], 0.4)}`,
                            flexShrink: 0,
                          }}
                        >
                          <EmojiEventsOutlinedIcon sx={{ fontSize: 16, color: medalColors[i] }} />
                        </Box>
                      ) : (
                        <Typography
                          variant="body1"
                          fontWeight={800}
                          sx={{
                            width: 32,
                            textAlign: 'center',
                            color: 'rgba(255,255,255,0.3)',
                            fontSize: '0.85rem',
                            flexShrink: 0,
                          }}
                        >
                          {entry.rank}
                        </Typography>
                      )}

                      {/* Avatar */}
                      <Avatar
                        src={entry.avatar || undefined}
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: '0.8rem',
                          border: isTop3 ? `2px solid ${alpha(medalColors[i], 0.5)}` : '2px solid rgba(255,255,255,0.08)',
                        }}
                      >
                        {(entry.display_name || entry.username)[0]}
                      </Avatar>

                      {/* Name */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {entry.display_name || entry.username}
                        </Typography>
                      </Box>

                      {/* Points */}
                      <Typography
                        variant="body1"
                        fontWeight={700}
                        sx={{
                          background: PULSE_GRADIENT,
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        {entry.contribution_points.toLocaleString()}
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{
                            WebkitTextFillColor: 'rgba(255,255,255,0.4)',
                            ml: 0.25,
                          }}
                        >
                          pt
                        </Typography>
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {/* ============================================================ */}
        {/* Tab 2: Points History */}
        {/* ============================================================ */}
        {tab === 2 && isAuthenticated && (
          <Box sx={{ maxWidth: 600, mx: 'auto', px: contentPx }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5,
                  background: alpha(PULSE_BLUE, 0.1),
                  border: `1px solid ${alpha(PULSE_BLUE, 0.2)}`,
                }}
              >
                <HistoryIcon sx={{ fontSize: 20, color: PULSE_BLUE }} />
              </Box>
              <Typography variant="body1" fontWeight={700}>
                ポイント履歴
              </Typography>
              {pointsHistory.length > 0 && (
                <Chip
                  icon={<StarIcon sx={{ fontSize: '14px !important' }} />}
                  label={`残高: ${pointsHistory[0].balance_after} pt`}
                  size="small"
                  sx={{
                    mt: 1,
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: 'primary.main',
                    border: 'none',
                    '& .MuiChip-icon': { color: 'primary.main' },
                  }}
                />
              )}
            </Box>

            {loadingPoints ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: '12px' }} />
                ))}
              </Box>
            ) : pointsHistory.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <HistoryIcon sx={{ fontSize: 24, color: 'rgba(255,255,255,0.2)' }} />
                </Box>
                <Typography variant="body2" fontWeight={500} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  まだポイント履歴がありません
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {pointsHistory.map(h => (
                    <Box
                      key={h.id}
                      sx={{
                        ...glassCardStatic,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        px: 2.5,
                        py: 1.5,
                      }}
                    >
                      {/* Icon indicator */}
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: h.amount > 0
                            ? alpha(theme.palette.primary.main, 0.1)
                            : alpha(theme.palette.error.main, 0.1),
                        }}
                      >
                        {h.amount > 0 ? (
                          <StarIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        ) : (
                          <SendIcon sx={{ fontSize: 14, color: 'error.main' }} />
                        )}
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {h.source_detail
                            ? (h.source_detail.kind === 'database'
                                ? `${h.source_detail.entity_label || 'キャラクター'}（${h.source_detail.is_edit ? '編集' : '作成'}）`
                                : `${h.source_detail.attribute_name}${h.source_detail.is_edit ? '（編集）' : ''}`)
                            : h.description || h.source_type}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
                          {new Date(h.created_at).toLocaleString('ja-JP')}
                        </Typography>
                      </Box>

                      <Typography
                        variant="body1"
                        fontWeight={700}
                        sx={{
                          color: h.amount > 0 ? 'primary.main' : 'error.main',
                        }}
                      >
                        {h.amount > 0 ? '+' : ''}{h.amount} pt
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {pointsCount > 20 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination
                      count={Math.ceil(pointsCount / 20)}
                      page={pointsPage}
                      onChange={(_, p) => setPointsPage(p)}
                      size="small"
                      sx={{
                        '& .MuiPaginationItem-root': {
                          borderRadius: '8px',
                          border: '1px solid rgba(255,255,255,0.08)',
                        },
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

export default HubPage;
